// PdfStyledTable.jsx
import { StyleSheet, Text, View } from "@react-pdf/renderer";
import { isArray, isObject, isString, isNil } from "lodash";
import PdfRow from "../layout/PdfRow";

const TABLE_BORDER_WIDTH = 1;
const TABLE_BORDER_RADIUS = 6;

const styles = StyleSheet.create({
  container: {
    marginTop: 0,
    marginBottom: 0,
    overflow: "hidden",
    borderRadius: TABLE_BORDER_RADIUS,
    border: `${TABLE_BORDER_WIDTH}px solid #ccc`,
  },
  title: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 6,
  },
  table: {
    display: "table",
    width: "100%",
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: `${TABLE_BORDER_WIDTH}px solid #ccc`,
  },
  headerRow: {
    backgroundColor: "#f0f0f0",
  },
  cellKey: {
    flex: 1,
    padding: 8,
    borderRightWidth: TABLE_BORDER_WIDTH,
    borderRightColor: "#ccc",
    borderRightStyle: "solid",
    fontSize: 8,
    fontWeight: "bold",
  },
  cellValue: {
    flex: 2,
    padding: 8,
    fontSize: 8,
    backgroundColor: "#f9fcff",
  },
  nestedContainer: {
    marginTop: 4,
    marginBottom: 4,
    paddingLeft: 8,
    borderLeft: "2px solid #e0e0e0",
  },
  nestedObjectTitle: {
    fontSize: 7,
    fontWeight: "bold",
    marginBottom: 2,
    color: "#666",
  },
  nestedItem: {
    marginBottom: 4,
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  nestedKey: {
    fontSize: 7,
    fontWeight: "bold",
    color: "#666",
    marginBottom: 1,
  },
  nestedValue: {
    fontSize: 7,
    color: "#333",
    marginLeft: 8,
  },
  arrayItemHeader: {
    fontSize: 7,
    fontWeight: "bold",
    color: "#666",
    marginBottom: 2,
    marginTop: 4,
  },
  bulletPoint: {
    fontSize: 7,
    color: "#333",
    marginLeft: 8,
    marginBottom: 2,
  },
});

// Helper to turn keys into nicer labels
const formatLabel = (key) => {
  if (!key) return "";
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Format value for display
const formatValue = (value) => {
  if (isNil(value)) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toLocaleString();
  if (value instanceof Date) return value.toLocaleDateString();
  return String(value);
};

// Component to render nested objects
const NestedObjectDisplay = ({ data, depth = 0 }) => {
  if (!isObject(data) || isArray(data)) {
    return <Text style={styles.nestedValue}>{formatValue(data)}</Text>;
  }

  return (
    <View style={[styles.nestedContainer, { marginLeft: depth * 8 }]}>
      {Object.entries(data).map(([key, value]) => (
        <View key={key} style={styles.nestedItem}>
          <Text style={styles.nestedKey}>{formatLabel(key)}:</Text>
          {isObject(value) && !isArray(value) && !isString(value) ? (
            <NestedObjectDisplay data={value} depth={depth + 1} />
          ) : isArray(value) ? (
            <NestedArrayDisplay data={value} depth={depth + 1} />
          ) : (
            <Text style={styles.nestedValue}>{formatValue(value)}</Text>
          )}
        </View>
      ))}
    </View>
  );
};

// Component to render arrays
const NestedArrayDisplay = ({ data, depth = 0 }) => {
  if (!isArray(data) || data.length === 0) {
    return <Text style={styles.nestedValue}>No items</Text>;
  }

  // Check if it's an array of objects
  const isObjectArray = data.every(
    (item) => isObject(item) && !isArray(item) && !isString(item)
  );

  if (isObjectArray) {
    return (
      <View style={[styles.nestedContainer, { marginLeft: depth * 8 }]}>
        {data.map((item, index) => (
          <PdfRow key={index}>
            <Text style={styles.arrayItemHeader}>Item {index + 1}:</Text>
            <NestedObjectDisplay data={item} depth={depth + 1} />
          </PdfRow>
        ))}
      </View>
    );
  }

  // Simple array of primitives
  return (
    <View style={[styles.nestedContainer, { marginLeft: depth * 8 }]}>
      {data.map((item, index) => (
        <Text key={index} style={styles.bulletPoint}>
          • {formatValue(item)}
        </Text>
      ))}
    </View>
  );
};

// Main table component
const PdfKeyValueTable = ({ data, title }) => {
  if (!data) return null;

  // Flatten nested data for better display
  const processData = (obj, prefix = "") => {
    const result = [];

    Object.entries(obj).forEach(([key, value]) => {
      const displayKey = prefix ? `${prefix}.${key}` : key;

      // Handle arrays and nested objects specially
      if (isArray(value)) {
        result.push({
          key: displayKey,
          value,
          isArray: true,
        });
      } else if (isObject(value) && !isString(value) && !isNil(value)) {
        result.push({
          key: displayKey,
          value,
          isNested: true,
        });
      } else {
        result.push({
          key: displayKey,
          value: formatValue(value),
          isSimple: true,
        });
      }
    });

    return result;
  };

  const entries = processData(data);

  return (
    <View style={styles.container}>
      {title ? <Text style={styles.title}>{title}</Text> : null}

      <View style={styles.table}>
        {entries.map((entry, index) => {
          const isLast = index === entries.length - 1;
          return (
            <View
              key={entry.key}
              style={{
                ...styles.tableRow,
                borderBottomWidth: isLast ? 0 : 1,
              }}
            >
              <View style={styles.cellKey}>
                <Text>{formatLabel(entry.key)}</Text>
              </View>
              <View
                style={{
                  ...styles.cellValue,
                  borderTopRightRadius: index === 0 ? TABLE_BORDER_RADIUS : 0,
                  borderBottomRightRadius: isLast ? TABLE_BORDER_RADIUS : 0,
                }}
              >
                {entry.isSimple ? (
                  <Text>{entry.value}</Text>
                ) : entry.isArray ? (
                  <NestedArrayDisplay data={entry.value} />
                ) : entry.isNested ? (
                  <NestedObjectDisplay data={entry.value} />
                ) : (
                  <Text>{String(entry.value)}</Text>
                )}
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
};

const PdfStyledTable = ({
  title,
  columnDefs = [],
  rowData = [],
  gap = 1,
  style,
  data,
}) => {
  return <PdfKeyValueTable data={data} title={title} />;
};

export default PdfStyledTable;
